import { cx, fmtNumber } from "utils";
import { PureTooltip } from "components/Tooltip";


function IndefiniteBarContent() {
  // TODO should come up with some better animations here
  return (
    <div className="absolute w-full h-[8px] bg-neutral-300 animate-pulse" />
  );
}

function Bar(
  {
    current,
    max,
    stackedValue,
    stackedTooltip,
  }: {
    current?: number,
    max?: number,
    stackedValue?: number,
    stackedTooltip?: string,
  }
) {
  let BarContent;

  if ((current !== undefined) && (max !== undefined)) {
    const monthlyProgress = Math.min(current, max) / max;
    const totalWithOverage = current + (stackedValue ?? 0);

    // If we have a stacked value (PAYG credits), show two segments
    if (stackedValue && stackedValue > 0) {
      // When at or over limit, show red for monthly
      const isOverMonthlyLimit = monthlyProgress >= 1;

      // Calculate widths as proportion of total credits used (monthly + PAYG)
      const monthlyRatio = (current / totalWithOverage) * 100;
      const overageRatio = (stackedValue / totalWithOverage) * 100;

      // Ensure both bars are always visible (minimum 2% each)
      // But also ensure total doesn't exceed 100%
      let monthlyWidthPercent = Math.max(2, monthlyRatio);
      let overageWidthPercent = Math.max(2, overageRatio);

      // If applying minimums causes total > 100%, scale down proportionally
      const total = monthlyWidthPercent + overageWidthPercent;
      if (total > 100) {
        monthlyWidthPercent = (monthlyWidthPercent / total) * 100;
        overageWidthPercent = (overageWidthPercent / total) * 100;
      }

      BarContent = (
        <>
          {/* Monthly credits bar (red if over limit, blue otherwise) */}
          <div
            className={ cx(
              "absolute left-0 h-[8px]",
              isOverMonthlyLimit ? "bg-error-600" : "bg-primary-600"
            ) }
            style={ {
              width: `${ monthlyWidthPercent }%`
            } }
          />
          {/* PAYG overage bar (yellow) - starts where monthly bar ends */}
          <div
            className="absolute h-[8px]"
            style={ {
              left: `${ monthlyWidthPercent }%`,
              width: `${ overageWidthPercent }%`
            } }
          >
            <PureTooltip content={ stackedTooltip }>
              <div
                className="absolute inset-0 bg-warning-500 cursor-default"
              />
            </PureTooltip>
          </div>
        </>
      );
    } else {
      // Single segment with color based on progress (no PAYG overages)
      // Per Asana requirements: turn red at 70% usage
      BarContent = (
        <div
          className={ cx(
            "absolute origin-left w-full h-[8px]",
            monthlyProgress < .5 && "bg-primary-600",
            monthlyProgress >= .5 && monthlyProgress < .7 && "bg-warning-600",
            monthlyProgress >= .7 && "bg-error-600"
          ) }
          style={ { transform: `scaleX(${ monthlyProgress })` } }
        />
      );
    }

  } else {
    // rendering indefinite progress bar
    BarContent = <IndefiniteBarContent />;
  }

  return (
    <div className="relative h-[8px] bg-transparent w-full">
      <div className="absolute w-full h-[8px] bg-neutral-100" />
      { BarContent }
    </div>
  );

}

export default function ProgressBar(
  {
    label,
    current,
    max,
    fullWidth = false,
    loading = false,
    stackedValue,
    stackedTooltip,
    valueSuffix,
  }: {
    label: string,
    current?: number,
    max?: number,
    fullWidth?: boolean,
    loading?: boolean,
    stackedValue?: number,
    stackedTooltip?: string,
    valueSuffix?: string,
  }
) {
  // Display the total (monthly + PAYG overage) in the value
  const totalCredits = current !== undefined && stackedValue !== undefined
    ? current + stackedValue
    : current;

  const totalLabel = totalCredits !== undefined ? fmtNumber(totalCredits) : "--";
  const limitLabel = max !== undefined ? fmtNumber(max) : "";
  const valueText = max !== undefined
    ? `${totalLabel} of ${limitLabel}${valueSuffix ? ` ${valueSuffix}` : ''}`
    : `${totalLabel}${valueSuffix ? ` ${valueSuffix}` : ''}`;

  return (
    <div className={ cx(
      "flex flex-col gap-y-2",
      fullWidth && "w-full",
      loading && "animate-pulse blur-[2px]"
    ) }
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-900">{ label }</span>
        <span className="text-sm text-neutral-900">{ valueText }</span>
      </div>
      <Bar current={ current } max={ max } stackedValue={ stackedValue } stackedTooltip={ stackedTooltip } />
    </div>
  );
};
